import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/use-auth';
import { Button } from '@/components/ui/button';
import { LogOut, Home, BarChart, Building, Settings, Building2, Map, Image as ImageIcon, Zap, FolderOpen } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { useSidebar } from '@/components/ui/sidebar-hook';
import { trackEvent } from '@/lib/analytics';
import { AdminFooter } from '@/components/admin/AdminFooter';

const adminNavItems = [
  { title: 'Analytics', url: '/admin/analytics', icon: BarChart, isImage: false },
  { title: 'HubSpot CRM', url: '/admin/hubspot', icon: '/hubspot-icon.png', isImage: true },
  { title: 'Unit Management', url: '/admin/units', icon: Building, isImage: false },
  { title: 'Image Management', url: '/admin/images', icon: ImageIcon, isImage: false },
  { title: 'Image Optimizer', url: '/admin/image-optimizer', icon: Zap, isImage: false },
  { title: 'Gallery Import', url: '/admin/gallery-import', icon: FolderOpen, isImage: false },
  { title: 'Map Tracer', url: '/admin/map-tracer', icon: Map, isImage: false },
  { title: 'Front Facing Tracer', url: '/admin/front-facing-tracer', icon: Building2, isImage: false },
  { title: 'Site Settings', url: '/admin/settings', icon: Settings, isImage: false },
];

function AdminSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const { isAdmin } = useAuth();
  const isCollapsed = state === 'collapsed';

  // Filter navigation items based on user role
  // Admin-only items: Image Management, Image Optimizer, Map Tracer, Front Facing Tracer, Site Settings
  const restrictedUrls = [
    '/admin/images',
    '/admin/image-optimizer',
    '/admin/gallery-import',
    '/admin/map-tracer',
    '/admin/front-facing-tracer',
    '/admin/settings'
  ];
  
  const visibleNavItems = isAdmin 
    ? adminNavItems 
    : adminNavItems.filter(item => !restrictedUrls.includes(item.url));

  const isActive = (url: string) => {
    if (url === '/admin/units') {
      return location.pathname.startsWith('/admin/units');
    }
    if (url === '/admin/images') {
      return location.pathname === '/admin/images';
    }
    if (url === '/admin/image-optimizer') {
      return location.pathname === '/admin/image-optimizer';
    }
    if (url === '/admin/gallery-import') {
      return location.pathname === '/admin/gallery-import';
    }
    if (url === '/admin/map-tracer') {
      return location.pathname === '/admin/map-tracer';
    }
    if (url === '/admin/front-facing-tracer') {
      return location.pathname === '/admin/front-facing-tracer';
    }
    return location.pathname === url;
  };

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarContent>
        {/* Logo Section */}
        <div className="p-4 border-b">
          {isCollapsed ? (
            <img
              src="/favicon.ico"
              alt="The Pearl at Grand Harbour"
              className="h-8 w-8 object-contain transition-all duration-200"
            />
          ) : (
            <img
              src="/logo-wh-x-gh.png"
              alt="The Pearl at Grand Harbour"
              className="h-12 w-auto object-contain transition-all duration-200"
            />
          )}
        </div>

        {/* Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Administration</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={isCollapsed ? item.title : undefined}
                  >
                    <Link to={item.url}>
                      {item.isImage ? (
                        <img src={item.icon as string} alt={item.title} className="h-4 w-4" />
                      ) : (
                        <item.icon className="h-4 w-4" />
                      )}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

export function AdminLayout() {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    trackEvent('admin_logout', {});
  };

  const handleGoToMainSite = () => {
    trackEvent('admin_navigate_to_main_site', {});
    navigate('/');
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AdminSidebar />
        
        <div className="flex-1 flex flex-col overflow-x-hidden min-w-0">
          {/* Header */}
          <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b bg-background px-4 py-3">
            <div className="flex items-center gap-4">
              <SidebarTrigger />
              <div>
                <h1 className="text-xl font-semibold">Site Administration</h1>
                <p className="text-sm text-muted-foreground hidden sm:block">
                  Manage site configuration and unit availability
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleGoToMainSite}
                className="hidden sm:flex"
              >
                <Home className="mr-2 h-4 w-4" />
                Main Site
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </main>

          {/* Footer */}
          <AdminFooter />
        </div>
      </div>
    </SidebarProvider>
  );
}
